import React, { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useApiKeyManager } from '../ApiKey/ApiKeyManagerProvider';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { trackEvent } from '../../utils/analytics';
import { PAGE_SEO, SEO_DEFAULTS } from '../../utils/seoConfig';
import { generateOpenClawIntegrationSchema, JsonLd } from '../../utils/structuredData';
import OpenClawHero from './sections/OpenClawHero';
import OpenClawProductHero from './sections/OpenClawProductHero';
import OpenClawHowItWorks from './sections/OpenClawHowItWorks';
import OpenClawEngineering from './sections/OpenClawEngineering';
import OpenClawUseCases from './sections/OpenClawUseCases';
import OpenClawFAQ from './sections/OpenClawFAQ';
import OpenClawClose from './sections/OpenClawClose';
import CrossDiscovery from './sections/CrossDiscovery';
import MobileStickyCta from '../Marketing/MobileStickyCta';
import './OpenClawLandingPage.css';

/**
 * OpenClawLandingPage — public marketing page at cellcog.ai/openclaw.
 *
 * Mirrors the /linear page structure but:
 *   - OpenClaw red color theme (not Linear purple)
 *   - OpenClaw × CellCog co-brand lockup
 *   - Narrative: any-to-any execution layer for OpenClaw agents
 *   - CTA is "Get CellCog API Key" → opens ApiKeyManager modal
 *     (Linear's CTA was "Connect" → OAuth redirect. Different action, same
 *      intent-preservation via signup return URL for logged-out users.)
 *
 * Lives at /openclaw (canonical, SEO-indexed). Management page for
 * existing OpenClaw users is /delegate/openclaw.
 *
 * Intent flow:
 *   logged-out → intent stashed via persistentNavigation (saveIntendedUrl) —
 *     the real post-auth routing rail; the old `?return=` query param on
 *     /signup was dead code (nothing read it), so post-signup visitors never
 *     returned here and the auto-open effect below never fired.
 *   logged-in → open ApiKeyManager modal directly (no redirect)
 */
const OpenClawLandingPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const { open: openApiKeyManager } = useApiKeyManager();

  // Ref used by the "See how it works" secondary CTA to smooth-scroll into
  // the How It Works section. Attached at the section root below.
  const howItWorksRef = useRef(null);

  const handleGetApiKey = useCallback((location) => {
    trackEvent('cta_click', {
      cta_name: 'get_api_key',
      location: typeof location === 'string' ? location : 'page',
      page: 'openclaw',
    });
    if (!currentUser) {
      // Preserve intent — on post-signup return we auto-open the modal.
      saveIntendedUrl('/openclaw?intent=get_api_key');
      navigate('/signup');
      return;
    }
    openApiKeyManager();
  }, [currentUser, navigate, openApiKeyManager]);

  // Post-signup auto-open: detect the ?intent=get_api_key query param
  // after the user lands back on /openclaw from the signup flow.
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('intent') === 'get_api_key') {
      // Cleanup URL before opening modal so a refresh doesn't re-trigger
      params.delete('intent');
      const url = new URL(window.location);
      url.search = params.toString();
      window.history.replaceState({}, document.title, url.toString());
      openApiKeyManager();
    }
  }, [currentUser, openApiKeyManager]);

  const handleSeeHowItWorks = useCallback(() => {
    if (howItWorksRef.current) {
      howItWorksRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Adaptive CTA label. Logged-out users signup to get a key; logged-in
  // users can either create their first or manage existing — "Get" reads
  // cleanly in both cases since the modal itself handles the branch.
  const ctaLabel = currentUser ? 'Get CellCog API Key' : 'Sign up to get API Key';

  const seo = PAGE_SEO.openclaw;
  const canonicalUrl = `${SEO_DEFAULTS.url}/openclaw`;
  const ogImage = `${SEO_DEFAULTS.url}/assets/openclaw/og-openclaw.png`;

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={ogImage} />
        {/* Inter Tight — scoped to this page for the hero gravitas. */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateOpenClawIntegrationSchema()} />

      <div className={`openclaw-landing ${isDarkMode ? 'dark' : 'light'}`}>
        <OpenClawHero
          ctaLabel={ctaLabel}
          onGetApiKey={() => handleGetApiKey('hero')}
          onSeeHowItWorks={handleSeeHowItWorks}
        />
        <OpenClawProductHero onGetApiKey={() => handleGetApiKey('product_hero')} ctaLabel={ctaLabel} />
        <OpenClawHowItWorks ref={howItWorksRef} />
        <OpenClawEngineering />
        <OpenClawUseCases />
        <OpenClawFAQ />
        <CrossDiscovery currentPage="openclaw" />
        <OpenClawClose
          ctaLabel={ctaLabel}
          onGetApiKey={() => handleGetApiKey('close')}
        />
        <MobileStickyCta label={ctaLabel} onClick={() => handleGetApiKey('sticky_bar')} />
      </div>
    </>
  );
};

export default OpenClawLandingPage;
